// RecentActivity.js - ACTUALIZADO con salesInvoices y purchaseInvoices
import React, { useState, useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Box,
  Chip,
  useTheme,
  Pagination,
  Stack,
} from "@mui/material";
import {
  TrendingUp,
  HourglassEmpty,
  Person,
  Description,
  Assignment,
  Receipt,
  CheckCircle,
  Schedule,
  Edit,
  Payments,
  LocalShipping,
  Handshake,
  Sell,
  ShoppingCart,
} from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";

const ITEMS_PER_PAGE = 5;

const RecentActivity = () => {
  const theme = useTheme();
  const { data: activities } = useCollection("activities");
  const [page, setPage] = useState(1);

  const getActivityIcon = (type) => {
    const iconMap = {
      proposals_add: <Description />,
      proposals_update: <Edit />,
      proposals_remove: <Edit />,
      contracts_add: <Assignment />,
      contracts_update: <Assignment />,
      contracts_remove: <Assignment />,
      projects_add: <Assignment />,
      projects_update: <Assignment />,
      projects_remove: <Assignment />,
      clients_add: <Person />,
      clients_update: <Edit />,
      clients_remove: <Edit />,
      salesInvoices_add: <Sell />,
      salesInvoices_update: <Sell />,
      salesInvoices_remove: <Edit />,
      purchaseInvoices_add: <ShoppingCart />,
      purchaseInvoices_update: <ShoppingCart />,
      purchaseInvoices_remove: <Edit />,
      contacts_add: <Person />,
      contacts_update: <Edit />,
      contacts_remove: <Edit />,
      companies_add: <Person />,
      companies_update: <Edit />,
      calendarEvents_add: <Assignment />,
      calendarEvents_update: <Edit />,
      calendarEvents_remove: <Edit />,
      tasks_add: <Assignment />,
      tasks_update: <Edit />,
      tasks_remove: <Edit />,
      suppliers_add: <LocalShipping />,
      suppliers_update: <Edit />,
      suppliers_remove: <Edit />,
      commercials_add: <Handshake />,
      commercials_update: <Edit />,
      commercials_remove: <Edit />,
    };

    const colorMap = {
      proposals_add: theme.palette.info.main,
      proposals_update: theme.palette.info.main,
      proposals_remove: theme.palette.error.main,
      contracts_add: theme.palette.secondary.main,
      contracts_update: theme.palette.secondary.main,
      contracts_remove: theme.palette.error.main,
      projects_add: theme.palette.primary.main,
      projects_update: theme.palette.primary.main,
      projects_remove: theme.palette.error.main,
      clients_add: theme.palette.warning.main,
      clients_update: theme.palette.warning.main,
      clients_remove: theme.palette.error.main,
      salesInvoices_add: "#0e502d",
      salesInvoices_update: "#0e502d",
      salesInvoices_remove: theme.palette.error.main,
      purchaseInvoices_add: "#f57c00",
      purchaseInvoices_update: "#f57c00",
      purchaseInvoices_remove: theme.palette.error.main,
      contacts_add: theme.palette.success.main,
      contacts_update: theme.palette.success.main,
      contacts_remove: theme.palette.error.main,
      companies_add: theme.palette.info.main,
      companies_update: theme.palette.info.main,
      calendarEvents_add: theme.palette.secondary.main,
      calendarEvents_update: theme.palette.secondary.main,
      tasks_add: theme.palette.primary.main,
      tasks_update: theme.palette.primary.main,
      suppliers_add: "#f57c00",
      suppliers_update: "#f57c00",
      suppliers_remove: theme.palette.error.main,
      commercials_add: "#0288d1",
      commercials_update: "#0288d1",
      commercials_remove: theme.palette.error.main,
    };

    const bgColorMap = {
      proposals_add: theme.palette.mode === "dark" ? "rgba(2, 136, 209, 0.15)" : "rgba(2, 136, 209, 0.1)",
      proposals_update: theme.palette.mode === "dark" ? "rgba(2, 136, 209, 0.15)" : "rgba(2, 136, 209, 0.1)",
      proposals_remove: theme.palette.mode === "dark" ? "rgba(211, 47, 47, 0.15)" : "rgba(211, 47, 47, 0.1)",
      contracts_add: theme.palette.mode === "dark" ? "rgba(0, 147, 93, 0.15)" : "rgba(0, 147, 93, 0.1)",
      contracts_update: theme.palette.mode === "dark" ? "rgba(0, 147, 93, 0.15)" : "rgba(0, 147, 93, 0.1)",
      contracts_remove: theme.palette.mode === "dark" ? "rgba(211, 47, 47, 0.15)" : "rgba(211, 47, 47, 0.1)",
      projects_add: theme.palette.mode === "dark" ? "rgba(0, 175, 163, 0.15)" : "rgba(0, 95, 101, 0.1)",
      projects_update: theme.palette.mode === "dark" ? "rgba(0, 175, 163, 0.15)" : "rgba(0, 95, 101, 0.1)",
      projects_remove: theme.palette.mode === "dark" ? "rgba(211, 47, 47, 0.15)" : "rgba(211, 47, 47, 0.1)",
      clients_add: theme.palette.mode === "dark" ? "rgba(237, 108, 2, 0.15)" : "rgba(237, 108, 2, 0.1)",
      clients_update: theme.palette.mode === "dark" ? "rgba(237, 108, 2, 0.15)" : "rgba(237, 108, 2, 0.1)",
      clients_remove: theme.palette.mode === "dark" ? "rgba(211, 47, 47, 0.15)" : "rgba(211, 47, 47, 0.1)",
      salesInvoices_add: theme.palette.mode === "dark" ? "rgba(14, 80, 45, 0.15)" : "rgba(14, 80, 45, 0.1)",
      salesInvoices_update: theme.palette.mode === "dark" ? "rgba(14, 80, 45, 0.15)" : "rgba(14, 80, 45, 0.1)",
      salesInvoices_remove: theme.palette.mode === "dark" ? "rgba(211, 47, 47, 0.15)" : "rgba(211, 47, 47, 0.1)",
      purchaseInvoices_add: theme.palette.mode === "dark" ? "rgba(245, 124, 0, 0.15)" : "rgba(245, 124, 0, 0.1)",
      purchaseInvoices_update: theme.palette.mode === "dark" ? "rgba(245, 124, 0, 0.15)" : "rgba(245, 124, 0, 0.1)",
      purchaseInvoices_remove: theme.palette.mode === "dark" ? "rgba(211, 47, 47, 0.15)" : "rgba(211, 47, 47, 0.1)",
      contacts_add: theme.palette.mode === "dark" ? "rgba(46, 125, 50, 0.15)" : "rgba(46, 125, 50, 0.1)",
      contacts_update: theme.palette.mode === "dark" ? "rgba(46, 125, 50, 0.15)" : "rgba(46, 125, 50, 0.1)",
      contacts_remove: theme.palette.mode === "dark" ? "rgba(211, 47, 47, 0.15)" : "rgba(211, 47, 47, 0.1)",
      companies_add: theme.palette.mode === "dark" ? "rgba(2, 136, 209, 0.15)" : "rgba(2, 136, 209, 0.1)",
      companies_update: theme.palette.mode === "dark" ? "rgba(2, 136, 209, 0.15)" : "rgba(2, 136, 209, 0.1)",
      calendarEvents_add: theme.palette.mode === "dark" ? "rgba(0, 147, 93, 0.15)" : "rgba(0, 147, 93, 0.1)",
      calendarEvents_update: theme.palette.mode === "dark" ? "rgba(0, 147, 93, 0.15)" : "rgba(0, 147, 93, 0.1)",
      tasks_add: theme.palette.mode === "dark" ? "rgba(0, 175, 163, 0.15)" : "rgba(0, 95, 101, 0.1)",
      tasks_update: theme.palette.mode === "dark" ? "rgba(0, 175, 163, 0.15)" : "rgba(0, 95, 101, 0.1)",
      suppliers_add: theme.palette.mode === "dark" ? "rgba(245, 124, 0, 0.15)" : "rgba(245, 124, 0, 0.1)",
      suppliers_update: theme.palette.mode === "dark" ? "rgba(245, 124, 0, 0.15)" : "rgba(245, 124, 0, 0.1)",
      suppliers_remove: theme.palette.mode === "dark" ? "rgba(211, 47, 47, 0.15)" : "rgba(211, 47, 47, 0.1)",
      commercials_add: theme.palette.mode === "dark" ? "rgba(2, 136, 209, 0.15)" : "rgba(2, 136, 209, 0.1)",
      commercials_update: theme.palette.mode === "dark" ? "rgba(2, 136, 209, 0.15)" : "rgba(2, 136, 209, 0.1)",
      commercials_remove: theme.palette.mode === "dark" ? "rgba(211, 47, 47, 0.15)" : "rgba(211, 47, 47, 0.1)",
    };

    return {
      icon: iconMap[type] || <Description />,
      color: colorMap[type] || theme.palette.primary.main,
      bgColor: bgColorMap[type] || (theme.palette.mode === "dark" ? "rgba(0, 175, 163, 0.15)" : "rgba(0, 95, 101, 0.1)"),
    };
  };

  const getEntityLabel = (entityType) => {
    const labels = {
      proposals: "Propuesta",
      contracts: "Contrato",
      projects: "Proyecto",
      clients: "Cliente",
      salesInvoices: "F. Venta",
      purchaseInvoices: "F. Compra",
      contacts: "Contacto",
      companies: "Empresa",
      calendarEvents: "Evento",
      tasks: "Tarea",
      suppliers: "Proveedor",
      commercials: "Comercial",
    };
    return labels[entityType] || entityType || "";
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "Reciente";
    try {
      const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
      const now = new Date();
      const diff = now - date;
      const mins = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (mins < 1) return "Ahora mismo";
      if (mins < 60) return `Hace ${mins} min`;
      if (hours < 24) return `Hace ${hours}h`;
      if (days < 7) return `Hace ${days}d`;

      return date.toLocaleDateString("es-ES", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Fecha no disponible";
    }
  };

  const sortedActivities = useMemo(() => {
    if (!activities) return [];
    return activities
      .filter((a) => a.isActive !== false)
      .sort((a, b) => {
        const dateA = a.timestamp?.toDate ? a.timestamp.toDate() : new Date(a.timestamp);
        const dateB = b.timestamp?.toDate ? b.timestamp.toDate() : new Date(b.timestamp);
        return dateB - dateA;
      });
  }, [activities]);

  const totalPages = Math.ceil(sortedActivities.length / ITEMS_PER_PAGE);

  const paginatedActivities = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return sortedActivities.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [sortedActivities, page]);

  React.useEffect(() => {
    setPage(1);
  }, [activities?.length]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: { xs: "12px", sm: "20px" },
        border: "1px solid",
        borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.12)" : "#e2e8f0",
        background: theme.palette.background.paper,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2.5, md: 3 }, flex: 1, display: "flex", flexDirection: "column" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: { xs: 1.5, sm: 2.5, md: 3 }, flexWrap: "wrap", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TrendingUp sx={{ color: theme.palette.primary.main, fontSize: { xs: 20, sm: 24 } }} />
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: "0.95rem", sm: "1.1rem", md: "1.25rem" } }}>
              Actividad Reciente
            </Typography>
          </Box>
          <Chip label={`${sortedActivities.length} actividades`} size="small" variant="outlined" sx={{ fontWeight: 500, fontSize: { xs: "0.65rem", sm: "0.75rem" } }} />
        </Box>

        {sortedActivities.length === 0 ? (
          <Box sx={{ py: { xs: 4, sm: 8 }, textAlign: "center", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <HourglassEmpty sx={{ fontSize: { xs: 36, sm: 48 }, color: "text.disabled", mb: 2 }} />
            <Typography color="text.secondary" fontWeight={500} sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}>
              No hay actividad reciente
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: { xs: "0.65rem", sm: "0.75rem" } }}>
              La actividad aparecerá aquí cuando se realicen cambios
            </Typography>
          </Box>
        ) : (
          <>
            <List sx={{ p: 0, flex: 1 }}>
              {paginatedActivities.map((activity, index) => {
                const { icon, color, bgColor } = getActivityIcon(activity.type);
                const entityLabel = getEntityLabel(activity.entityType);
                return (
                  <React.Fragment key={activity.id || index}>
                    {index > 0 && <Divider component="li" sx={{ ml: { xs: 6, sm: 7 } }} />}
                    <ListItem alignItems="flex-start" sx={{ px: { xs: 0.5, sm: 1 }, py: { xs: 1.5, sm: 2 }, borderRadius: 2, transition: "all 0.2s ease", "&:hover": { backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)" } }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: bgColor, color: color, width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 } }}>
                          <Box sx={{ display: "flex", "& .MuiSvgIcon-root": { fontSize: { xs: "1.25rem", sm: "1.5rem" } } }}>{icon}</Box>
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box>
                            <Typography variant="body2" sx={{ lineHeight: 1.5, fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                              <Box component="span" fontWeight={700} color="primary.main">{activity.userName || "Usuario"}</Box>{" "}
                              <Box component="span" color="text.secondary">{activity.description || "realizó una acción"}</Box>
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5, flexWrap: "wrap", gap: 0.5 }}>
                            <Typography variant="caption" color="text.disabled" sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: { xs: "0.6rem", sm: "0.7rem" } }}>
                              <Schedule sx={{ fontSize: { xs: 10, sm: 12 } }} />
                              {formatTime(activity.timestamp)}
                            </Typography>
                            {entityLabel && (
                              <Chip label={entityLabel} size="small" variant="outlined" sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: "0.55rem", sm: "0.65rem" }, fontWeight: 500, borderColor: color, color: color }} />
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2, pt: 2, borderTop: "1px solid", borderColor: theme.palette.divider }}>
                <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" size="small" showFirstButton showLastButton siblingCount={0} boundaryCount={1} sx={{ "& .MuiPaginationItem-root": { borderRadius: "10px", fontWeight: 600, fontSize: { xs: "0.7rem", sm: "0.8rem" }, minWidth: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }, "& .Mui-selected": { backgroundColor: `${theme.palette.primary.main} !important`, color: "white" } }} />
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentActivity;